<template>
	<div
		:class="[
			selectable ? 'cursor-pointer hover:border-won-green' : '',
			isSelected ? 'pdf-preview-card--selected border-won-green' : 'border-[#c2c4c6]',
		]"
		class="
			h-[207px] w-[168px] rounded border bg-white transition-colors
			focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-won-green
		"
		:role="selectable ? 'radio' : undefined"
		:aria-checked="selectable ? isSelected : undefined"
		:tabindex="selectable ? 0 : undefined"
		@click="handleSelect(preview)"
		@keydown.enter="handleKeySelect"
		@keydown.space="handleKeySelect"
	>
		<component
			:is="opensEditor ? 'router-link' : 'div'"
			v-bind="opensEditor ? { to: editRoute, 'aria-label': `Edit ${extractContent(preview.title) || 'Title'}` } : {}"
			class="pdf-preview relative block w-full px-1.5 pt-2.5"
		>
			<div
				v-if="isPrimaryResume"
				class="
					absolute top-1 left-1 z-10 flex items-center gap-1 rounded-sm
					bg-won-green-light px-2 py-1 text-won-green
				"
			>
				<StarIcon class="size-4" />
				<span class="text-p-xs font-semibold">
					Primary
				</span>
			</div>

			<CheckCircleIcon
				v-if="selectable && isSelected"
				class="pdf-preview-check absolute top-1 right-1 z-10 size-6 rounded-full bg-white text-won-green"
			/>

			<img
				data-categories="essential"
				:src="
					preview?.preview
						? preview.preview
						: isCoverLetter ? '/img/pdf-preview-coverletter.png' : '/img/pdf-preview.png'
				"
				@error="handleErrorLoad"
			/>

			<div
				v-if="opensEditor"
				class="pdf-preview-action hidden"
			>
				<span class="pdf-preview-edit-pill font-noto rounded px-2.5 font-medium text-white">
					<edit color="white" />

					<span>
						Edit
					</span>
				</span>
			</div>
		</component>

		<div class="pdf-labels">
			<div
				class="grid w-full grid-cols-2 place-content-center items-center"
				style="grid-template-columns: 1fr 16px"
			>
				<input
					v-if="rename"
					ref="renameEl"
					v-model="renameValue"
					class="won-blue w-full p-0 text-sm font-medium focus:outline-hidden"
					@blur="handleEndRename"
					@keypress.enter="handleEndRename"
				/>

				<div
					v-else
					class="won-blue w-full truncate text-sm font-medium"
				>
					{{ extractContent(preview.title) || 'Title' }}
				</div>
				
				<preview-action
					v-if="!hideActions"
					:is-won-format="!preview?.keep_original_format"
					:is-primary="isPrimaryResume"
					:is-cover-letter="isCoverLetter"
					@preview-actions:downloadpdf="() => handleDownload(DOCUMENT_PDF)"
					@preview-actions:rename="handleRename"
					@preview-actions:delete="handleDelete"
					@preview-actions:duplicate="duplicateDispatcher"
					@preview-actions:set-primary="setPrimaryResumeHandler"
					@preview-actions:set-new-primary="primaryResumeStore.setShowSelectPrimaryResume"
				/>
				<div
					v-else
					class="cursor-pointer"
					:class="{ 'pointer-coarse:hidden': selectable }"
					@click.stop="handleOpenPreview"
				>
					<svg
						width="19"
						height="18"
						viewBox="0 0 19 18"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>            <g clip-path="url(#clip0_1670_50940)">            <path
						d="M14.7499 14.25H4.24994V3.75H9.49994V2.25H4.24994C3.41744 2.25 2.74994 2.925 2.74994 3.75V14.25C2.74994 15.075 3.41744 15.75 4.24994 15.75H14.7499C15.5749 15.75 16.2499 15.075 16.2499 14.25V9H14.7499V14.25ZM10.9999 2.25V3.75H13.6924L6.31994 11.1225L7.37744 12.18L14.7499 4.8075V7.5H16.2499V2.25H10.9999Z"
						fill="#8897AA"
					/>            </g>            <defs>            <clipPath id="clip0_1670_50940">            <rect
						width="18"
						height="18"
						fill="white"
						transform="translate(0.500061)"
					/>            </clipPath>            </defs>            </svg>
				</div>
			</div>

			<div class="won-label-gray mt-1 text-xs">
				{{ docCreationDate }}
			</div>
		</div>
	</div>
  
	<delete-modal
		:key="deleteVisible"
		:visible="deleteVisible"
		:title="`Delete \`${extractContent(preview.title) || 'Title'}\``"
		:loading="deleteLoading"
		subtitle="Are you sure you want to delete this resume?"
		@delete-modal:close="handleCloseDelete"
		@delete-modal:confirm="handleDeleteConfirm"
	/>
</template>

<script setup>
import { computed, onMounted, ref, toRefs } from 'vue';
import { loadRouteLocation, useRouter, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useDateFormat } from '@vueuse/core';
import { CheckCircleIcon, StarIcon } from '@heroicons/vue/24/solid';
import { extractContent } from '../../utils/helpers';

import { DOCUMENT_PDF } from '../../store/builder-style.store';
import { DEFAULT_DATE_FORMAT } from '../../utils/constants';

import { useBuilder } from '../../store/builder.store';
import { useResumesStore } from '../../store/resumes.store';
import { useCoverLettersStore } from '../../store/cover-letters.store';
import { usePrimaryResumeStore } from '../../store/primary-resume.store';
import { useNotifications } from '../../store/notifications.store';

import {
	retrieveResume,
	updateResume,
} from '../../api/builder';

import {
	createCoverLetter,
	retrieveCoverLetter,
	updateCoverLetter
} from '../../api/cover-letters';

import PreviewAction from './preview-action.vue';
import Edit from '../../icons/actions/edit.vue';
import DeleteModal from '../../modals/delete-modal.vue';


const props = defineProps({
	isCoverLetter: {
		type: Boolean,
		default: false
	},
	selected: {
		type: Boolean,
		default: false
	},
	hideActions: {
		type: Boolean,
		default: false
	},
	selectable: {
		type: Boolean,
		default: false
	},
	preview: {
		type: Object,
		default: () => ({}),
	},
});

const emits = defineEmits(['pdf-preview:refresh', 'selected']);

const { isCoverLetter, hideActions, selectable, selected, preview } = toRefs(props);

const renameEl = ref(null);
const rename = ref(false);
const renameValue = ref('');

const isSelected = computed(() => selected.value);

const deleteVisible = ref(false);
const deleteLoading = ref(false);

const router = useRouter();
const builder = useBuilder();
const notificationsStore = useNotifications();
const resumesStore = useResumesStore();
const coverLetterStore = useCoverLettersStore();
builder.activeRoute = useRoute();

const docCreationDate = useDateFormat(preview.value.created_at, DEFAULT_DATE_FORMAT);

const primaryResumeStore = usePrimaryResumeStore();
const { primaryResume } = storeToRefs(primaryResumeStore);

const { downloadFileObject } = storeToRefs(builder);

const isPrimaryResume = computed(() => {
	return preview.value?.uuid === primaryResume.value?.uuid;
});

const isDownloadDisabled = computed(() => {
	return downloadFileObject.value?.uploading || false;
});

// Display cards (/resumes, /cover-letters) open the editor from the thumbnail; picker cards select instead.
const opensEditor = computed(() => !selectable.value && !hideActions.value);

const editRoute = computed(() => ({
	name: isCoverLetter.value ? 'cover-letters.edit' : 'resumes.edit',
	params: { id: preview.value.uuid },
}));

// The editor is a lazy route; fetch it now so the click isn't held up by the download. Swallow a failure:
// left unhandled, a chunk error would hit app.js's unhandledrejection reload and reload this page. The
// real navigation retries the fetch, and router.onError handles its chunk errors.
onMounted(() => {
	if (opensEditor.value) {
		loadRouteLocation(router.resolve(editRoute.value)).catch(() => {});
	}
});

const handleSelect = (item) => {
	if (!selectable.value) {
		return;
	}

	emits('selected', item);
};

// Only a picker card claims Enter/Space. On a display card, preventing them would stop the keyboard from
// opening the thumbnail link and stop the rename input from taking a space.
const handleKeySelect = (event) => {
	if (!selectable.value) {
		return;
	}

	event.preventDefault();
	handleSelect(preview.value);
};

const handleOpenPreview = () => {
	const routeData = router.resolve(editRoute.value);
	window.open(routeData.href, '_blank');
};

const handleErrorLoad = (event) => {
	event.target.src = isCoverLetter.value ? '/img/pdf-preview-coverletter.png' : '/img/pdf-preview.png';
};

const handleRename = () => {
	renameValue.value = extractContent(preview.value.title);
	rename.value = true;

	setTimeout(() => {
		renameEl.value.focus();
	}, 10);
};

const setPrimaryResumeHandler = () => {
	primaryResumeStore.setPrimaryResume(preview.value.uuid);
};

const handleEndRename = async () => {
	rename.value = false;
	preview.value.title = renameValue.value;

	const resume = isCoverLetter.value ? await retrieveCoverLetter(preview.value.uuid) : await retrieveResume(preview.value.uuid);

	isCoverLetter.value
		? updateCoverLetter(preview.value.uuid, { ...resume, title: preview.value.title })
		: updateResume(preview.value.uuid, { ...resume, title: preview.value.title });
};

const handleDelete = () => {
	deleteVisible.value = true;
};

const handleCloseDelete = () => {
	deleteVisible.value = false;
};

const handleDeleteConfirm = async () => {
	deleteLoading.value = true;

	try {
		if (isCoverLetter.value) {
			await coverLetterStore.deleteCoverLetter(preview.value.uuid);
		} else {
			await resumesStore.deleteResume(preview.value.uuid);
		}
	} catch {
		notificationsStore.notify(`Failed to delete ${isCoverLetter.value ? 'cover letter' : 'resume'}!`, 'error');
	} finally {
		handleCloseDelete();
		deleteLoading.value = false;
	}
};

const duplicateDispatcher = () => {
	if (isCoverLetter.value) {
		duplicateCoverLetterHandler();
	} else {
		duplicateResumeHandler();
	}
};

const duplicateResumeHandler = async () => {
	const resumeId = preview.value.uuid;

	await resumesStore.duplicateResume(resumeId);
};

const duplicateCoverLetterHandler = async () => {
	const result = await retrieveCoverLetter(preview.value.uuid);
	const parsed = builder.buildData(result, true);

	const data = {
		...parsed,
		title: `${parsed.title  } - Copy`,
		id: undefined,
	};

	await createCoverLetter(data);
	emits('pdf-preview:refresh');
};

const handleDownload = (format) => {
	if (isDownloadDisabled.value) {return;}

	builder.download(preview.value.uuid, format);
};
</script>

<style scoped>
/*
 * Inset box-shadow rather than a wider border or an outer ring: the card has a fixed
 * 168x207 box (a 2px border would reflow it) and the picker grids are `overflow-y-auto`,
 * which clips an outer ring horizontally.
 */
.pdf-preview-card--selected {
  box-shadow: inset 0 0 0 2px var(--color-won-green);
  background-color: var(--color-won-green-hover);
}

.pdf-preview {
  overflow: hidden;
  height: 139px;
  position: relative;
}

/*
 * Hover-capable devices only: on touch the first tap would only reveal the overlay (iOS also
 * cancels a tap that reveals clickable content), so the thumbnail link opens on a single tap.
 */
@media (hover: hover) {
  .pdf-preview:hover > .pdf-preview-action {
    display: flex;
  }
}

/* The global `* { outline: none }` reset hides outlines, so the overlay doubles as the focus cue. */
.pdf-preview:focus-visible > .pdf-preview-action {
  display: flex;
}

.pdf-preview-action {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  justify-content: center;
  align-items: center;
}

.pdf-preview-edit-pill {
  background-color: #505d68;
  display: flex;
  align-items: center;
  height: 22px;
  font-size: 12px;
}

.pdf-preview-edit-pill svg {
  width: 14px;
  height: 14px;
  margin-right: 4px;
}

.pdf-preview > img {
  width: 100%;
  object-position: top;
}

.pdf-labels {
  padding: 13px 8px 13px 15px;
}
</style>
